import { lentComponent, useContractMutation, useContractQuery, useInvalidateContract } from "@amtana/browser";
import type { LentToken, MutationResult, QueryResult } from "@amtana/browser";
import { enrichmentTrpcContract } from "@amtana/enrichment-contract";
import type { EnrichmentPreview, EnrichmentRequest, EnrichmentRun } from "@amtana/enrichment-contract";

// AI enrichment for every browser module, and the enrich dialog it lends.

export type EnrichDialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly scope: EnrichmentRequest["scope"];
};

export const EnrichDialog: LentToken<EnrichDialogProps> = lentComponent<EnrichDialogProps>(
  "enrichment",
  "dialog",
);

export function useEnrichmentPreview(
  request: EnrichmentRequest,
  options: { enabled: boolean },
): QueryResult<EnrichmentPreview> {
  return useContractQuery(enrichmentTrpcContract, "preview", { input: request, enabled: options.enabled });
}

export function useEnrichmentRun(
  runId: string | undefined,
  options: { live: boolean },
): QueryResult<EnrichmentRun> {
  return useContractQuery(enrichmentTrpcContract, "get", {
    input: { runId: runId ?? "" },
    enabled: runId !== undefined,
    refetchInterval: options.live ? 1000 : false,
  });
}

/** Enrichment changes prospects and balances, so every read refreshes after it. */
export function useStartEnrichment(): MutationResult<EnrichmentRun, EnrichmentRequest> {
  const mutation = useContractMutation(enrichmentTrpcContract, "start");
  const invalidate = useInvalidateContract();
  return {
    ...mutation,
    run: (input: EnrichmentRequest) => mutation.mutateAsync(input).finally(() => void invalidate()),
  };
}

export function useCancelEnrichment(): MutationResult<EnrichmentRun, { runId: string }> {
  const mutation = useContractMutation(enrichmentTrpcContract, "cancel");
  const invalidate = useInvalidateContract();
  return {
    ...mutation,
    run: (input: { runId: string }) => mutation.mutateAsync(input).finally(() => void invalidate()),
  };
}
